/**
 * On-device clue database loader.
 *
 * The full SQLite DB (~108MB) is split into gzipped chunks (see
 * data-pipeline/split_db.py) to stay under Cloudflare Pages' 25 MiB per-file
 * limit. On first launch we fetch every chunk, gunzip with DecompressionStream,
 * concatenate, and open it with sql.js (in-memory SQLite compiled to WASM).
 * The assembled bytes are cached in IndexedDB keyed by the manifest version, so
 * subsequent launches (and offline use inside Capacitor) are instant.
 *
 * sql.js holds the whole DB in WASM memory. ~108MB is fine on modern phones;
 * if that ever becomes a problem the migration path is wa-sqlite + OPFS for
 * true on-disk paging, behind this same module's interface.
 */
import type { Database, SqlJsStatic } from 'sql.js';
// Pin the classic build (not the "browser" export, which ignores locateFile and
// expects its wasm adjacent — Vite doesn't emit that, causing a 404 at boot).
import initSqlJs from 'sql.js/dist/sql-wasm.js';
// Vite resolves this to a hashed asset URL for the WASM binary.
import wasmUrl from 'sql.js/dist/sql-wasm.wasm?url';

interface Manifest {
  version: string;
  chunkCount: number;
  dbBytes: number;
  chunks: number[];
}

const DB_BASE = `${import.meta.env.BASE_URL}data/db`;
const IDB_NAME = 'jeopardy-db-cache';
const IDB_STORE = 'db';

export type ProgressFn = (loaded: number, total: number, phase: string) => void;

let SQL: SqlJsStatic | null = null;

async function getSqlJs(): Promise<SqlJsStatic> {
  if (!SQL) SQL = await initSqlJs({ locateFile: () => wasmUrl });
  return SQL;
}

function idb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(IDB_STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(key: string): Promise<Uint8Array | undefined> {
  const db = await idb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readonly');
    const req = tx.objectStore(IDB_STORE).get(key);
    req.onsuccess = () => resolve(req.result as Uint8Array | undefined);
    req.onerror = () => reject(req.error);
  });
}

async function idbPut(key: string, val: Uint8Array): Promise<void> {
  const db = await idb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).put(val, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function idbClearExcept(keepKey: string): Promise<void> {
  const db = await idb();
  const tx = db.transaction(IDB_STORE, 'readwrite');
  const store = tx.objectStore(IDB_STORE);
  const keys = await new Promise<IDBValidKey[]>((res, rej) => {
    const r = store.getAllKeys();
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
  for (const k of keys) if (k !== keepKey) store.delete(k);
}

async function gunzip(buf: ArrayBuffer): Promise<Uint8Array> {
  const ds = new DecompressionStream('gzip');
  const stream = new Response(buf).body!.pipeThrough(ds);
  const out = await new Response(stream).arrayBuffer();
  return new Uint8Array(out);
}

async function fetchAndAssemble(manifest: Manifest, onProgress?: ProgressFn): Promise<Uint8Array> {
  const full = new Uint8Array(manifest.dbBytes);
  let offset = 0;
  for (let i = 0; i < manifest.chunkCount; i++) {
    const url = `${DB_BASE}/chunk-${String(i).padStart(3, '0')}.bin`;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`chunk ${i} fetch failed: ${resp.status}`);
    const chunk = await gunzip(await resp.arrayBuffer());
    full.set(chunk, offset);
    offset += chunk.length;
    onProgress?.(i + 1, manifest.chunkCount, 'Downloading clue bank');
  }
  if (offset !== manifest.dbBytes) {
    throw new Error(`assembled ${offset} bytes, expected ${manifest.dbBytes}`);
  }
  return full;
}

/** Load the clue DB, using the IndexedDB cache when the version matches. */
export async function openClueDb(onProgress?: ProgressFn): Promise<Database> {
  onProgress?.(0, 1, 'Preparing');
  const manifest: Manifest = await fetch(`${DB_BASE}/manifest.json`).then((r) => r.json());
  const cacheKey = `db:${manifest.version}`;

  let bytes = await idbGet(cacheKey);
  if (!bytes) {
    bytes = await fetchAndAssemble(manifest, onProgress);
    onProgress?.(manifest.chunkCount, manifest.chunkCount, 'Saving for offline use');
    try {
      await idbPut(cacheKey, bytes);
      await idbClearExcept(cacheKey); // drop stale versions
    } catch {
      // Cache write can fail under storage pressure — non-fatal, we still have bytes.
    }
  } else {
    onProgress?.(1, 1, 'Loading from cache');
  }

  const sql = await getSqlJs();
  return new sql.Database(bytes);
}
